import { Component, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { DatePipe } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { FormService } from 'src/app/shared/services/form.service';
import { ChartOptions, Chart, ChartData, ChartConfiguration, ChartDataset } from 'chart.js';
import { SharedService } from 'src/app/features/service/shared.service';
import { maintainRatio } from 'src/app/shared/util/graph.utils';
import DragDataPlugin from 'chartjs-plugin-dragdata';
import { BaseChartDirective } from 'ng2-charts';
import { FigureOrderBucket } from 'src/app/shared/models/FigureOrderItem';
import { UserService } from 'src/app/shared/services/user.service';
import { AzureAdB2C } from 'src/environments/environment';
import { NotesComponent } from 'src/app/shared/components/notes/notes.component';
import { HttpErrorResponse } from '@angular/common/http';
import { ReportService } from '../../../services/native-report.service';
import { NativeSoilRecommendation } from '../../../models/native-soil-recommendation.model';

Chart.register(DragDataPlugin);

interface FigureScale {
  maxX: number | null;
  maxY: number | null;
}

interface CurvePoint {
  moistureContent: number;
  tr: number;
}

interface FigureObject {
  maxX: number | null;
  maxY: number | null;
  legendHeader: string | null;
  figureNumber: number;
  curveShown: boolean;
}

export interface NativeFigureCurve {
  sampleLabel: string;
  groupID: string;
  curvePoints: CurvePoint[];
  figurePosition: number;
  isShown: boolean;
  dryDensity: number;
  soilDescription: string;
  threshold: number;
  radius: number;
  showPredictedCurve: boolean;
  predictedPoints : CurvePoint[];
  recommendation?: NativeSoilRecommendation;
  predictionError?: string;
}

export interface NativeFigureObject extends FigureObject {
  nativeFigureCurves: NativeFigureCurve[];
}

const EMPTY_SCALE = (): FigureScale => ({
  maxX: null,
  maxY: null,
});

type ErrorBody = { error?: string } | string | null | undefined;

const tryParseJson = (value: string): ErrorBody => {
  try {
    return JSON.parse(value) as ErrorBody;
  } catch {
    return value;
  }
};

const extractErrorMessage = (error: HttpErrorResponse): string => {
  let body: ErrorBody = error?.error;
  for (let depth = 0; depth < 2 && typeof body === 'string'; depth += 1) {
    body = tryParseJson(body);
  }
  if (typeof body === 'string' && body) return body;
  if (body && typeof body !== 'string' && body.error) return body.error;
  return error?.message ?? 'Prediction failed.';
};

@Component({
    selector: 'app-thermal-resistivity-native-soil',
    templateUrl: './thermal-resistivity-native-soil.component.html',
    styleUrls: ['./thermal-resistivity-native-soil.component.scss'],
    providers: [DatePipe],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ThermalResistivityNativeSoilComponent implements OnInit {
  private static readonly MAX_POINTS_PER_FIGURE = 10;

  private static readonly DEFAULT_RADIUS = 50;

  private static readonly DEFAULT_THRESHOLD = 0.9;

  private static readonly POINTS_TO_PX = 96 / 72;

  readonly chartWidthPt = 424.636456692913;

  readonly chartHeightPt = 496.816771652543;

  readonly chartWidthPx = this.chartWidthPt * ThermalResistivityNativeSoilComponent.POINTS_TO_PX * 1.2;

  readonly chartHeightPx = this.chartHeightPt * ThermalResistivityNativeSoilComponent.POINTS_TO_PX * 1.2;

  private static readonly CHART_BORDER_PX = 1;

  readonly chartBoxWidthPx = this.chartWidthPx + (ThermalResistivityNativeSoilComponent.CHART_BORDER_PX * 2);

  private static readonly LINE_COLORS = ['#FF0000',
    '#00B050',
    '#00B0F0',
    '#7030A0',
    '#984807',
    '#000000',
  ];

  @ViewChild(BaseChartDirective) chart?: BaseChartDirective;

  projectId = '';
  soilType = '';
  projectName = '';
  companyName = '';
  projectLocation = '';
  isMetricValue: boolean;

  userRoles: string[] = [];

  drawerActive = false;
  prevDisabled = true;
  nextDisabled = false;

  pageIndex = -1;
  numberOfFigures = 0;

  chartData: ChartData<'line'> = {
    datasets: [{
      data: [],
    }],
  };

  chartOptions: ChartConfiguration<'line'>['options'] = {
    responsive: false,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      x: {
        type: 'linear',
      },
    },
  };

  figureOrderingPopupOpen = false;
  figureNavOpen = true;

  figureNumbers : number[] = [];
  figures : NativeFigureObject[] = [];
  currentFigure: NativeFigureObject;
  visible: {
    nativeFigureCurve: NativeFigureCurve;
    color: string;
  }[];

  visiblePredicted: {
    nativeFigureCurve: NativeFigureCurve;
    color: string;
  }[];

  readonly minRadius = 1;
  readonly maxRadius = 99;
  readonly minThreshold = 0;
  readonly maxThreshold = 1;

  constructor(
    public activatedRoute: ActivatedRoute,
    public userService: UserService,
    public dialog: MatDialog,
    private reportService: ReportService,
    private formService: FormService,
    private sharedService: SharedService,
  ) {}

  ngOnInit(): void {
    this.userAccountAndRole();
    this.soilType = 'nativesoil';
    this.projectId = this.activatedRoute.snapshot.paramMap.get('id') ?? '';
    this.getProjectInfo(this.projectId, this.soilType);
    this.sharedService.soilTypes(this.soilType);
  }

  userAccountAndRole(): void {
    this.userService.userRole?.subscribe({
      next: (roles) => {
        this.userRoles = roles || [];
      },
    });
  }

  roleCheck(roles: string[]): boolean {
    return roles.some((role) => this.userRoles.includes(role));
  }

  openWorksheet(): void {
    const url = `${AzureAdB2C.redirectUri}worksheet?soilType=${this.soilType}&&projectId=${this.projectId}`;
    window.open(url, '_blank');
  }

  openViewSubmissionForm(): void {
    const url = `${AzureAdB2C.redirectUri}review/form/${this.soilType}/${this.projectId}`;
    window.open(url, '_blank');
  }

  openNotesDialog(): void {
    this.dialog.open(NotesComponent, {
      panelClass: 'app-dialog',
      data: {
        projectId: this.projectId,
      },
      disableClose: true,
      width: '60%',
    });
  }

  getProjectInfo(projectId: string, soilType: string): void {
    this.formService?.fetchFormData(projectId, soilType)?.subscribe({
      next: (data) => {
        this.reportService.projectInformation = data;
        this.projectName = data.project.projectName;
        this.companyName = data.company.name;
        this.projectLocation = data.project.projectLocation ?? '';
        this.getReportInfo();
      },
      error: () => {},
    });
  }

  getReportInfo(): void {
    this.reportService.fetchReportData(this.projectId).subscribe({
      next: (data) => {
        this.isMetricValue = data.isMetricUnit;
        this.getGraphData();
      },
    });
  }

  getGraphData(): void {
    this.reportService.getGraphData(this.projectId)?.subscribe({
      next: (graphData) => {
        this.pageIndex = -1;
        this.figures = graphData;
        this.figures.forEach((figure) => {
          figure.nativeFigureCurves.forEach((curve) => {
            curve.isShown = true;
            curve.radius = ThermalResistivityNativeSoilComponent.DEFAULT_RADIUS;
            curve.threshold = ThermalResistivityNativeSoilComponent.DEFAULT_THRESHOLD;
            curve.showPredictedCurve = true;
            curve.predictionError = '';
            curve.predictedPoints = [];
          });
        });
        this.numberOfFigures = this.figures.length;
        this.setPage(0);
      },
      error: () => {},
    });
  }

  getCurveColor(curveIndex: number): string {
    const colors = ThermalResistivityNativeSoilComponent.LINE_COLORS;
    return colors[curveIndex % colors.length];
  }

  onCurveShownChange(event: Event): void {
    this.currentFigure.curveShown = (event.target as HTMLInputElement).checked;
  }

  toggleCurveVisibility(nativeFigureCurve : NativeFigureCurve): void {
    nativeFigureCurve.isShown = !nativeFigureCurve.isShown;
    this.setChartData();
  }

  togglePredictedCurveVisibility(nativeFigureCurve : NativeFigureCurve): void {
    nativeFigureCurve.showPredictedCurve = !nativeFigureCurve.showPredictedCurve;
    this.setChartData();
  }

  setChartData(): void {
    const colors = ThermalResistivityNativeSoilComponent.LINE_COLORS;
    const withColors = (this.currentFigure?.nativeFigureCurves ?? [])
      .map((nativeFigureCurve, index) => ({
        nativeFigureCurve, color: colors[index % colors.length],
      }));

    this.visible = withColors.filter((item) => item.nativeFigureCurve.isShown);

    this.visiblePredicted = withColors.filter(
      (item) => item.nativeFigureCurve.showPredictedCurve && item.nativeFigureCurve.predictedPoints?.length,
    );

    const originals = this.visible.map(
      (item) => ThermalResistivityNativeSoilComponent.toDataset(item, item.nativeFigureCurve.curvePoints, false),
    );

    const predicted = this.visiblePredicted.map(
      (item) => ThermalResistivityNativeSoilComponent.toDataset(item, item.nativeFigureCurve.predictedPoints, true),
    );

    this.chartData = {
      ...this.chartData,
      datasets: [...originals, ...predicted],
    };
    this.chartOptions = this.setChartOptions();
    this.chart?.update();
  }

  private static toDataset(
    item: { nativeFigureCurve: NativeFigureCurve; color: string },
    curvePoints: CurvePoint[],
    isPredicted: boolean,
  ): ChartDataset<'line'> {
    const { sampleLabel } = item.nativeFigureCurve;
    return {
      label: isPredicted ? `${sampleLabel} (Predicted)` : sampleLabel,
      data: (curvePoints ?? [])
        .filter((curvePoint) => Number.isFinite(curvePoint.moistureContent) && Number.isFinite(curvePoint.tr))
        .map((curvePoint) => ({
          x: curvePoint.moistureContent,
          y: curvePoint.tr,
        })),
      tension: 0.4,
      borderColor: item.color,
      backgroundColor: item.color,
      pointBackgroundColor: item.color,
      pointRadius: 5,
      borderDash: isPredicted ? [6, 4] : [],
      dragData: !isPredicted,
    };
  }

  roundUpBy5 = (num: number): number => Math.ceil(num / 5) * 5;
  roundUpBy50 = (num: number): number => Math.ceil(num / 50) * 50;
  roundUpByPoint5 = (num: number): number => Math.ceil(num / 0.5) * 0.5;

  onMaxXChange(event: Event): void {
    const value = parseFloat((event.target as HTMLInputElement).value);
    if (Number.isNaN(value)) {
      this.applyMaxScale(EMPTY_SCALE());
      return;
    }
    const ratio = this.isMetricValue ? 0.1 : 10;
    const scaleX = Math.max(15, this.roundUpBy5(value));
    this.applyMaxScale({
      maxX: scaleX,
      maxY: ratio * scaleX,
    });
  }

  onMaxYChange(event: Event): void {
    const value = parseFloat((event.target as HTMLInputElement).value);
    if (Number.isNaN(value)) {
      this.applyMaxScale(EMPTY_SCALE());
      return;
    }
    const ratio = this.isMetricValue ? 0.1 : 10;
    const nextY = this.isMetricValue ? this.roundUpByPoint5(value) : this.roundUpBy50(value);
    const scaleY = Math.max(this.isMetricValue ? 1.5 : 50, nextY);
    this.applyMaxScale({
      maxX: scaleY / ratio,
      maxY: scaleY,
    });
  }

  applyScaleToAllFigures(): void {
    const { maxX, maxY } = this.currentFigure;
    this.reportService
      .updateAllFiguresScales(this.projectId, maxX ?? null, maxY ?? null)
      .subscribe({
        next: () => {
          this.figures.forEach((figure) => {
            figure.maxX = maxX;
            figure.maxY = maxY;
          });
          this.setChartData();
        },
      });
  }

  private applyMaxScale(scale: FigureScale): void {
    this.currentFigure.maxX = scale.maxX;
    this.currentFigure.maxY = scale.maxY;
    this.setChartData();
  }

  private getGraphScale(): { y: number; x: number } {
    const scale = {
      maxX: this.currentFigure.maxX, maxY: this.currentFigure.maxY,
    };
    if (scale?.maxX != null && scale?.maxY != null) {
      return {
        x: scale.maxX,
        y: scale.maxY,
      };
    }
    const allPoints = this.currentFigure.nativeFigureCurves
      .flatMap((nativeFigureCurve) => [
        ...(nativeFigureCurve.curvePoints ?? []),
        ...(nativeFigureCurve.showPredictedCurve ? nativeFigureCurve.predictedPoints ?? [] : []),
      ])
      .filter((p) => Number.isFinite(p.moistureContent) && Number.isFinite(p.tr));
    const maxMc = Math.max(0, ...allPoints.map((p) => p.moistureContent));
    const maxTr = Math.max(0, ...allPoints.map((p) => p.tr));
    return maintainRatio(maxMc, maxTr, this.isMetricValue);
  }

  setChartOptions(): ChartOptions<'line'> {
    const scale = this.getGraphScale();
    const trText = this.isMetricValue ? 'THERMAL RESISTIVITY (°K.m/W)' : 'THERMAL RESISTIVITY (°C -cm/W)';
    const yGridStep = this.isMetricValue ? 0.1 : 10;
    const yLabelStep = this.isMetricValue ? 0.5 : 50;
    return {
      responsive: false,
      maintainAspectRatio: false,
      plugins: {
        dragData: {
          dragX: true,
          dragY: true,
          showTooltip: true,
          onDrag: (_e: any, _di: number, pointIndex: number, value: any) => {
            if (pointIndex === 0 && value && typeof value === 'object') {
              value.x = 0;
            }
            return undefined;
          },
          onDragEnd: (_e: any, datasetIndex: number, pointIndex: number, value: any) => {
            if (value == null || typeof value !== 'object') return;
            const item = this.visible[datasetIndex];
            if (!item?.nativeFigureCurve.curvePoints?.[pointIndex]) return;
            item.nativeFigureCurve.curvePoints[pointIndex].moistureContent = value.x;
            item.nativeFigureCurve.curvePoints[pointIndex].tr = value.y;
          },
        },
        legend: {
          // Rendered as an HTML section outside the canvas (see `legendItems`) so
          // the plotted graph never resizes as legend entries are added.
          display: false,
        },
        tooltip: {
          enabled: true,
          displayColors: false,
          callbacks: {
            title: () => '',
            label: (item) => `MC: ${item.parsed.x} %`,
            afterLabel: (item) => `TR: ${item.parsed.y}`,
          },
        },
        title: {
          display: true,
          text: 'THERMAL DRYOUT CURVE',
          color: 'black',
        },
      },
      scales: {
        y: {
          min: 0,
          max: scale.y,
          title: {
            display: true,
            text: trText,
            color: 'black',
            font: {
              weight: 'bold', size: 16,
            },
          },
          ticks: {
            stepSize: yGridStep,
            autoSkip: false,
            callback: (value, indexTick) => {
              if (value === 0 && indexTick === 0) return '';
              const ratio = Number(value) / yLabelStep;
              return Math.abs(ratio - Math.round(ratio)) < 1e-9 ? value : '';
            },
            major: {
              enabled: true,
            },
            color: 'black',
            maxRotation: 0,
            minRotation: 0,
          },
        },
        x: {
          type: 'linear',
          min: 0,
          max: scale.x,
          title: {
            display: true,
            text: 'MOISTURE CONTENT (% DRY WEIGHT)',
            color: 'black',
            font: {
              weight: 'bold', style: 'normal', size: 16,
            },
          },
          ticks: {
            stepSize: 1,
            autoSkip: false,
            includeBounds: true,
            precision: 0,
            callback: (value, indexTick) => {
              if (value === 0 && indexTick === 0) return '0    ';
              return value === scale.x || indexTick % 5 === 0 ? value : '';
            },
            major: {
              enabled: true,
            },
            color: 'black',
            maxRotation: 0,
            minRotation: 0,
          },
          grid: {
            drawTicks: true,
            drawOnChartArea: true,
            tickBorderDash: [0],
          },
        },
      },
    };
  }

  next(): void {
    this.setPage(this.pageIndex + 1);
  }

  prev(): void {
    this.setPage(this.pageIndex - 1);
  }

  goToFigure(index: number): void {
    this.setPage(index);
  }

  private setPage(index: number): void {
    if (index < 0 || index >= this.numberOfFigures || index === this.pageIndex) return;
    this.pageIndex = index;
    this.currentFigure = this.figures[index];
    this.setChartData();
    this.navigationButtonCheck();
  }

  toggleFigureNav(): void {
    this.figureNavOpen = !this.figureNavOpen;
    setTimeout(() => this.chart?.chart?.resize());
  }

  updatePoint(curveIndex: number, pointIndex: number, axis: 'x' | 'y', event: Event): void {
    const value = parseFloat((event.target as HTMLInputElement).value);
    if (Number.isNaN(value)) return;
    const points = this.currentFigure.nativeFigureCurves[curveIndex].curvePoints;
    if (!points?.[pointIndex]) return;
    if (axis === 'x') points[pointIndex].moistureContent = value;
    if (axis === 'y') points[pointIndex].tr = value;
    this.refreshCurrentChart();
  }

  deletePoint(curveIndex: number, pointIndex: number): void {
    const points = this.currentFigure.nativeFigureCurves[curveIndex].curvePoints;
    if (!points || points.length <= 1) return;
    points.splice(pointIndex, 1);
    this.refreshCurrentChart();
  }

  addPointBetween(curveIndex: number, prevIndex: number): void {
    const points = this.currentFigure.nativeFigureCurves[curveIndex].curvePoints;
    if (!points || points.length >= ThermalResistivityNativeSoilComponent.MAX_POINTS_PER_FIGURE) return;
    const prev = points[prevIndex];
    const next = points[prevIndex + 1];
    if (!prev || !next) return;
    points.splice(prevIndex + 1, 0, {
      moistureContent: (prev.moistureContent + next.moistureContent) / 2,
      tr: (prev.tr + next.tr) / 2,
    });
    this.refreshCurrentChart();
  }

  private refreshCurrentChart(): void {
    this.setChartData();
  }

  canAddPoint(curveIndex: number): boolean {
    const points = this.currentFigure.nativeFigureCurves[curveIndex].curvePoints;
    return !!points && points.length < ThermalResistivityNativeSoilComponent.MAX_POINTS_PER_FIGURE;
  }

  onRadiusChange(nativeFigureCurve: NativeFigureCurve, event: Event): void {
    const value = parseFloat((event.target as HTMLInputElement).value);
    if (Number.isNaN(value)) return;
    nativeFigureCurve.radius = value;
  }

  onThresholdChange(nativeFigureCurve: NativeFigureCurve, event: Event): void {
    const value = parseFloat((event.target as HTMLInputElement).value);
    if (Number.isNaN(value)) return;
    nativeFigureCurve.threshold = value;
  }

  predict(nativeFigureCurve: NativeFigureCurve): void {
    this.runPrediction(
      nativeFigureCurve,
      nativeFigureCurve.radius ?? ThermalResistivityNativeSoilComponent.DEFAULT_RADIUS,
      nativeFigureCurve.threshold ?? ThermalResistivityNativeSoilComponent.DEFAULT_THRESHOLD,
    );
  }

  predictWithoutRadiusAndThreshold(nativeFigureCurve: NativeFigureCurve): void {
    this.runPrediction(nativeFigureCurve, null, null);
  }

  private runPrediction(
    nativeFigureCurve: NativeFigureCurve,
    radius: number | null,
    threshold: number | null,
  ): void {
    const description = (nativeFigureCurve.soilDescription ?? '').trim();
    const location = (this.projectLocation ?? '').trim();
    const moistures = (nativeFigureCurve.curvePoints ?? []).map((curvePoint) => curvePoint.moistureContent);

    nativeFigureCurve.predictionError = undefined;

    if (!description) {
      nativeFigureCurve.predictionError = 'This sample has no soil description to predict from.';
      return;
    }
    if (!location) {
      nativeFigureCurve.predictionError = 'This project has no location to predict from.';
      return;
    }
    if (!moistures.length) {
      nativeFigureCurve.predictionError = 'This sample has no moisture points to predict from.';
      return;
    }

    this.reportService
      .getNativeSoilRecommendation({
        description,
        location,
        radius,
        dryDensity: nativeFigureCurve.dryDensity ?? 0,
        moistures,
        threshold,
      })
      .subscribe({
        next: (result) => {
          if ('error' in result) {
            nativeFigureCurve.predictionError = result.error;
            return;
          }
          nativeFigureCurve.recommendation = result;
          nativeFigureCurve.predictedPoints = result.points.map((point) => ({
            moistureContent: point.avg_moisture,
            tr: point.avg_tr,
          }));
          nativeFigureCurve.showPredictedCurve = true;
          this.setChartData();
        },
        error: (error: HttpErrorResponse) => {
          nativeFigureCurve.predictionError = extractErrorMessage(error);
        },
      });
  }

  navigationButtonCheck(): void {
    this.prevDisabled = this.pageIndex === 0;
    this.nextDisabled = this.pageIndex === this.numberOfFigures - 1;
  }

  onSubmit(): void {
    const figureObject = {
      nativeFigureCurves: (this.currentFigure?.nativeFigureCurves ?? []).map((curve) => ({
        groupID: curve.groupID,
        curvePoints: curve.curvePoints.map((point) => ({
          moistureContent: point.moistureContent,
          tr: point.tr,
        })),
      })),
    };
    this.reportService.updateNativeSoilWorksheets(this.projectId, this.isMetricValue, figureObject).subscribe({
      next: (curves : CurvePoint[][]) => {
        curves.forEach((curve, index) => {
          this.currentFigure.nativeFigureCurves[index].curvePoints = curve;
        });
        this.setChartData();
      },
    });
    this.reportService
      .updateFigureProperty(
        this.projectId,
        this.currentFigure.figureNumber,
        this.currentFigure.maxX ?? null,
        this.currentFigure.maxY ?? null,
        this.currentFigure.curveShown,
      )
      .subscribe();
  }

  openFigureOrderingPopup(): void {
    this.figureOrderingPopupOpen = true;
  }

  closeFigureOrderingPopup(): void {
    this.figureOrderingPopupOpen = false;
  }

  saveFigureOrdering(event: {
    figures: NativeFigureObject[];
    reordered: boolean;
  }): void {
    this.figureOrderingPopupOpen = false;
    const { figures, reordered } = event;
    const figureOrderingBucket : { [bucket: number]: FigureOrderBucket } = {
    };
    this.figures = figures;
    figures.forEach((figure) => {
      figureOrderingBucket[figure.figureNumber] = {
        legendHeader: figure.legendHeader,
        figures: figure.nativeFigureCurves.map((curve, idx) => ({
          groupId: curve.groupID,
          figurePosition: idx,
        })),
      };
    });
    this.reportService.orderNativeSoilFigures(figureOrderingBucket, this.projectId, reordered).subscribe({
      next: () => {
        if (reordered) {
          this.getGraphData();
        }
      },
    });
  }

  previewGraphs(): void {
    this.reportService.createNativeSoilGraphs(this.isMetricValue, this.projectId, this.currentFigure.figureNumber).subscribe({
      next: (pdfBlob: Blob) => {
        const url = URL.createObjectURL(pdfBlob);
        window.open(url, '_blank');
      },
    });
  }

  openNav(): void {
    this.setDrawerWidth('250px', true);
  }

  closeNav(): void {
    this.setDrawerWidth('0', false);
  }

  private setDrawerWidth(width: string, active: boolean): void {
    const sidenav = document.getElementById('drawer');
    if (sidenav) {
      sidenav.style.width = width;
      this.drawerActive = active;
    }
  }
}
